import type { printOnDemand, ID } from "common/utils/models";
import { useCompositionsActions } from "@design-lab/entities/composition";
import { Button } from "@design-lab/shared/ui";
import { PlusCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useRef, useState, useEffect, useCallback } from "react";
import { CompositionPreviewImage } from "./CompositionPreviewImage";

type CompositionsCarouselProps = {
  compositions: printOnDemand.getProduct.Composition[];
  selectedId: ID | undefined;
  onSelect: (id: ID) => void;
  onAddMoreProduct?: (() => void) | undefined;
};

export function CompositionsCarousel(props: CompositionsCarouselProps) {
  const { compositions, selectedId, onSelect, onAddMoreProduct } = props;
  const { removeComposition } = useCompositionsActions();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const compositionsToDisplay = compositions.filter(composition =>
    Boolean(composition.surfaces?.front?.picture || composition.surfaces?.frontLeftChest?.picture)
  );
  const showRemove = compositionsToDisplay.length > 1;

  const handleRemove = (id: ID) => {
    removeComposition(id);
  };

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !container.parentElement) return;

    const containerWidth = container.scrollWidth;
    const visibleWidth = container.parentElement.clientWidth;
    const maxScroll = Math.max(0, containerWidth - visibleWidth);

    setCanScrollLeft(scrollPosition > 0);
    setCanScrollRight(scrollPosition < maxScroll && maxScroll > 0);
  }, [scrollPosition]);

  const scrollLeft = () => {
    const newPosition = Math.max(0, scrollPosition - 120);
    setScrollPosition(newPosition);
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container || !container.parentElement) return;

    const containerWidth = container.scrollWidth;
    const visibleWidth = container.parentElement.clientWidth;
    const maxScroll = Math.max(0, containerWidth - visibleWidth);
    const newPosition = Math.min(maxScroll, scrollPosition + 120);
    setScrollPosition(newPosition);
  };

  // Reset scroll position when compositions change
  useEffect(() => {
    setScrollPosition(0);
  }, [compositionsToDisplay.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    // Check initial scrollability
    checkScrollability();

    // Add resize observer to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => checkScrollability());
    });

    resizeObserver.observe(container);

    const cleanup = () => {
      resizeObserver.disconnect();
    };

    return cleanup;
  }, [compositionsToDisplay, checkScrollability]);

  return (
    <div className="relative -bottom-0.5 flex items-center">
      {canScrollLeft && (
        <Button
          variant="secondary"
          className="h-13 lg:h-18 absolute left-0 top-2.5 z-10 w-10 lg:top-3 lg:w-11"
          data-testid="scroll-left-button"
          onClick={scrollLeft}
        >
          <ChevronLeft className="size-6" />
        </Button>
      )}
      <div className="flex-1 overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="flex items-center transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${scrollPosition}px)` }}
        >
          {compositionsToDisplay.map(composition => (
            <CompositionPreviewImage
              key={composition.id}
              composition={composition}
              selected={composition.id === selectedId}
              onClick={() => onSelect(composition.id)}
              onRemove={showRemove ? handleRemove : undefined}
            />
          ))}
        </div>
      </div>

      {canScrollRight && (
        <Button
          variant="secondary"
          className="h-13 lg:h-18 absolute right-0 top-2.5 z-10 w-10 lg:top-3 lg:w-11"
          data-testid="scroll-right-button"
          onClick={scrollRight}
        >
          <ChevronRight className="size-6" />
        </Button>
      )}

      {onAddMoreProduct && (
        <Button
          data-testid="add-products-button"
          onPress={onAddMoreProduct}
          className="size-13 ml-2 bg-blue-700 hover:bg-blue-800"
        >
          <PlusCircle className="size-5" />
        </Button>
      )}
    </div>
  );
}
